import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { X, Upload, Image as ImageIcon, Video, FileText, Trash2, Play, Eye, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { useGetAssetsQuery, useUploadAssetMutation, useDeleteAssetMutation, Asset, AssetType } from '../../features/assets/assetsApi';
import { normalizeId } from '../../utils/assetUtils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export type FileType = 'image' | 'video' | 'document';

// UI Interface for uploaded files, compatible with existing drag-drop logic
export interface UploadedFile {
  id: string;
  name: string;
  type: FileType;
  thumbnail: string;
  uploadProgress: number; // For identifying uploading state
  status: 'uploading' | 'completed' | 'error';
  file?: File;
  // Store the raw asset for advanced usage
  rawAsset?: Asset;
  isLocal?: boolean; // Track if it's a local optimistic update
  width?: number;    // Optional: dimensions coming from API/library
  height?: number;   // Optional
}

interface UploadsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToCanvas?: (file: UploadedFile, dropX?: number, dropY?: number, overrideMuted?: boolean) => void;
}

type FilterTab = 'all' | 'images' | 'videos' | 'documents';

export function UploadsPanel({ isOpen, onClose, onAddToCanvas }: UploadsPanelProps) {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  // State for delete confirmation
  const [fileToDelete, setFileToDelete] = useState<{ id: string, isLocal: boolean } | null>(null);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
  const [previewMuted, setPreviewMuted] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [loadingThumbnails, setLoadingThumbnails] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (previewFile) {
      setIsPreviewLoading(true);
    }
  }, [previewFile?.id]);

  const handleImageLoad = (id: string) => {
    setLoadingThumbnails(prev => ({ ...prev, [id]: false }));
  };

  const handleImageError = (id: string) => {
    setLoadingThumbnails(prev => ({ ...prev, [id]: false }));
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local state for actively uploading files (optimistic UI)
  const [uploadingFiles, setUploadingFiles] = useState<UploadedFile[]>([]);

  // API Hooks
  // We'll fetch all types for now, or filter by tab if strict
  const { data: assetsData, isLoading } = useGetAssetsQuery({
    limit: 50,
    type: activeTab === 'all' ? undefined :
      activeTab === 'images' ? 'image' :
        activeTab === 'videos' ? 'video' :
          activeTab === 'documents' ? 'font' : undefined // Basic mapping for now
  });

  const [uploadAsset] = useUploadAssetMutation();
  const [deleteAsset] = useDeleteAssetMutation();


  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Process each file
    for (const file of files) {
      const fileType = getFileType(file);
      if (!fileType) continue;

      // Create a temporary ID for local state
      const tempId = `temp-${Date.now()}-${Math.random()}`;

      const newUpload: UploadedFile = {
        id: tempId,
        name: file.name,
        type: fileType,
        thumbnail: '', // Will be set by preview
        uploadProgress: 0,
        status: 'uploading',
        file,
        isLocal: true
      };

      // Generate preview
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadingFiles(prev => prev.map(u =>
            u.id === tempId ? { ...u, thumbnail: e.target?.result as string } : u
          ));
        };
        reader.readAsDataURL(file);
      }

      setUploadingFiles(prev => [newUpload, ...prev]);

      // Perform upload
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);

        await uploadAsset(formData).unwrap();

        // Remove from local uploading state on success (API list will update)
        setUploadingFiles(prev => prev.filter(u => u.id !== tempId));
      } catch (err) {
        console.error('Upload failed:', err);
        setUploadingFiles(prev => prev.map(u =>
          u.id === tempId ? { ...u, status: 'error', uploadProgress: 0 } : u
        ));
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileType = (file: File): FileType | null => {
    const type = file.type;
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type === 'application/pdf') return 'document';
    return null;
  };

  // Map API assets to UI format
  const apiUploads: UploadedFile[] = (assetsData?.data || []).map(asset => ({
    id: normalizeId(asset._id),
    name: asset.name,
    type: mapAssetType(asset.type),
    thumbnail: asset.type === 'video' ? (asset.thumbnail || asset.url) : (asset.url || asset.thumbnail || ''),
    uploadProgress: 100,
    status: 'completed',
    rawAsset: asset,
    isLocal: false
  }));

  function mapAssetType(assetType: AssetType): FileType {
    switch (assetType) {
      case 'image': return 'image';
      case 'video': return 'video';
      case 'font': return 'document'; // Mapping font/vector to document/others for now
      case 'vector': return 'image';
      default: return 'document';
    }
  }

  // Combine local uploading files with API files
  const allFiles = [...uploadingFiles, ...apiUploads];

  const handleDeleteClick = (id: string, isLocal: boolean) => {
    setFileToDelete({ id, isLocal });
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    const { id, isLocal } = fileToDelete;

    if (isLocal) {
      setUploadingFiles(prev => prev.filter(u => u.id !== id));
    } else {
      await deleteAsset(id);
    }
    setFileToDelete(null);
  };

  const handleRetry = async (id: string) => {
    const upload = uploadingFiles.find(u => u.id === id);
    if (upload && upload.file) {
      setUploadingFiles(prev => prev.map(u =>
        u.id === id ? { ...u, status: 'uploading' } : u
      ));

      try {
        const formData = new FormData();
        formData.append('file', upload.file);
        formData.append('name', upload.name);
        await uploadAsset(formData).unwrap();
        setUploadingFiles(prev => prev.filter(u => u.id !== id));
      } catch (err) {
        console.error('Retry failed:', err);
        setUploadingFiles(prev => prev.map(u =>
          u.id === id ? { ...u, status: 'error' } : u
        ));
      }
    }
  };

  const filteredUploads = allFiles.filter(upload => {
    if (activeTab === 'all') return true;
    if (activeTab === 'images') return upload.type === 'image';
    if (activeTab === 'videos') return upload.type === 'video';
    if (activeTab === 'documents') return upload.type === 'document';
    return true;
  });

  const getFileIcon = (type: FileType) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'document': return <FileText className="w-4 h-4" />;
    }
  };
  const handleDragStart = (e: React.DragEvent, upload: UploadedFile) => {
    if (upload.status !== 'completed') return;
    e.dataTransfer.setData('element-type', 'image');
    // Pass essential data, ensuring url/thumbnail is present
    e.dataTransfer.setData('uploaded-file', JSON.stringify({
      ...upload,
      thumbnail: upload.thumbnail || upload.rawAsset?.url // Fallback if needed
    }));
    e.dataTransfer.effectAllowed = 'copy';

    // Create a drag image
    const dragImg = new Image();
    dragImg.src = upload.thumbnail;
    dragImg.width = 50;
    dragImg.height = 50;
    e.dataTransfer.setDragImage(dragImg, 25, 25);
  };

  return (
    <>


      {/* Panel */}
      <div
        className={`fixed left-[98px] top-[80px] w-[340px] h-[calc(100vh-100px)] bg-white rounded-2xl shadow-xl z-40 flex flex-col transition-opacity duration-300 ease-in-out border border-gray-100 overflow-hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between border-b border-gray-100 bg-white z-10">
          <div>
            <h2 className="font-semibold text-gray-900 text-lg">Uploads</h2>
            <p className="text-xs text-gray-500 mt-0.5">Upload images, videos, and files</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 custom-scrollbar">

          {/* Upload Button */}
          <div className="py-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full bg-[#30A84B] hover:bg-[#2a9643] active:scale-[0.98] text-white px-4 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
            >
              <Upload className="w-4 h-4" />
              <span className="font-medium">Upload Files</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/svg+xml,image/webp,video/mp4,video/webm,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1.5 mb-4">
            {(['all', 'images', 'videos', 'documents'] as FilterTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={clsx(
                  "px-3 py-2 rounded-lg text-xs font-medium transition-all relative overflow-hidden group capitalize",
                  activeTab === tab
                    ? "app-active-state"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                )}
              >
                <div 
                    className="app-selector-indicator" 
                    style={{ '--background-width': activeTab === tab ? '100%' : '0%' } as React.CSSProperties} 
                />
                <span className="relative z-10 transition-colors duration-400">{tab}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-8 h-8 border-4 border-[#30A84B] border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-gray-500 mt-2">Loading assets...</p>
            </div>
          ) : filteredUploads.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <Upload className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="font-medium text-gray-900 mb-2">No uploads yet</h3>
              <p className="text-sm text-gray-500">Upload content to use in your designs</p>
            </div>
          ) : (
            /* Upload Grid */
            <div className="grid grid-cols-2 gap-3">
              {filteredUploads.map(upload => (
                <div
                  key={upload.id}
                  draggable={upload.status === 'completed'}
                  onDragStart={(e) => handleDragStart(e, upload)}
                  className="group relative aspect-square bg-gray-50 rounded-lg overflow-hidden border border-transparent hover:border-[#30A84B]/30 transition-all shadow-sm hover:shadow-md"
                >
                  {/* Thumbnail Container */}
                  <div className="w-full h-full flex items-center justify-center relative">
                    {upload.status === 'uploading' && (
                      <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2 z-10">
                        <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                        <div className="text-white text-[10px] font-medium">Uploading...</div>
                      </div>
                    )}

                    {upload.status === 'error' && (
                      <div className="absolute inset-0 bg-red-50 flex flex-col items-center justify-center gap-2 z-10">
                        <div className="text-red-500 text-xs font-medium">Failed</div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRetry(upload.id); }}
                          className="text-xs text-red-600 hover:text-red-700 underline"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {upload.thumbnail ? (
                      <div className="relative w-full h-full flex items-center justify-center bg-gray-50">
                        {loadingThumbnails[upload.id] !== false && (
                          <div className="absolute inset-0 flex items-center justify-center z-10">
                            <Loader2 className="w-5 h-5 text-teal-600 animate-spin opacity-50" />
                          </div>
                        )}
                        {upload.thumbnail.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/) || upload.type === 'video' ? (
                          <div className="w-full h-full relative">
                            {loadingThumbnails[upload.id] !== false && (
                              <div className="absolute inset-0 flex items-center justify-center z-10">
                                <Loader2 className="w-5 h-5 text-teal-600 animate-spin opacity-50" />
                              </div>
                            )}
                            <video
                              src={upload.thumbnail}
                              className={`w-full h-full object-cover rounded-md bg-black ${loadingThumbnails[upload.id] !== false ? 'opacity-0' : 'opacity-100'}`}
                              muted
                              playsInline
                              preload="auto"
                              crossOrigin="anonymous"
                              onLoadedData={() => handleImageLoad(upload.id)}
                              onCanPlay={() => handleImageLoad(upload.id)}
                              onError={() => handleImageError(upload.id)}
                            />
                            {loadingThumbnails[upload.id] === false && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-black/20 rounded-full p-1.5 shadow-sm group-hover:scale-110 transition-transform">
                                  <Play className="w-4 h-4 text-white fill-current" />
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <img
                            src={upload.thumbnail}
                            alt={upload.name}
                            crossOrigin="anonymous"
                            className={`w-full h-full object-cover transition-opacity duration-300 ${loadingThumbnails[upload.id] !== false ? 'opacity-0' : 'opacity-100'}`}
                            onLoad={() => handleImageLoad(upload.id)}
                            onError={() => handleImageError(upload.id)}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#F9FAFB] text-gray-400 group-hover:bg-[#F3F4F6] transition-colors">
                        {upload.type === 'video' ? (
                          <div className="relative">
                            <Video className="w-8 h-8" />
                            <Play className="w-3 h-3 absolute -bottom-1 -right-1 text-teal-600 fill-current" />
                          </div>
                        ) : getFileIcon(upload.type)}
                        <span className="text-[10px] uppercase font-bold tracking-wider opacity-60">{upload.type}</span>
                      </div>
                    )}

                    {/* Hover Actions */}
                    {upload.status === 'completed' && (
                      <div
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(upload.id, !!upload.isLocal); }}
                          className="bg-white hover:bg-gray-100 text-red-600 p-1.5 rounded-md transition-colors shadow-sm"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {upload.type === 'video' && (
                          <div className="flex flex-col items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); setPreviewFile(upload); setPreviewMuted(false); }}
                              className="p-1.5 bg-white shadow-lg rounded-full text-blue-600 hover:text-blue-700 hover:scale-110 transition-all"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <span className="text-[8px] font-bold text-white uppercase tracking-tighter">Preview</span>
                          </div>
                        )}
                        <div className="flex flex-col items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); onAddToCanvas?.(upload); }}
                            className="p-1.5 bg-white shadow-lg rounded-full text-teal-600 hover:text-teal-700 hover:scale-110 transition-all"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <span className="text-[8px] font-bold text-white uppercase tracking-tighter">Add</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar (Bottom) - Only for active uploads */}
                  {upload.status === 'uploading' && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                      <div
                        className="h-full bg-[#30A84B] transition-all duration-200"
                        style={{ width: '50%' }} // Infinite loading state since API doesn't give progress
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!fileToDelete} onOpenChange={(open: boolean) => !open && setFileToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Video Preview Modal */}
      <AlertDialog open={!!previewFile} onOpenChange={(open: boolean) => !open && setPreviewFile(null)}>
        <AlertDialogContent className="max-w-2xl bg-black border-none p-0 overflow-hidden rounded-2xl">
          <div className="relative aspect-video w-full bg-black group/preview">
            {previewFile && (
              <>
                <video
                  src={previewFile.rawAsset?.url || previewFile.thumbnail}
                  autoPlay
                  loop
                  muted={previewMuted}
                  crossOrigin="anonymous"
                  className={`w-full h-full object-contain transition-opacity duration-300 ${isPreviewLoading ? 'opacity-0' : 'opacity-100'}`}
                  playsInline
                  controls
                  onWaiting={() => setIsPreviewLoading(true)}
                  onPlaying={() => setIsPreviewLoading(false)}
                  onCanPlayThrough={() => setIsPreviewLoading(false)}
                  onLoadStart={() => setIsPreviewLoading(true)}
                  onLoadedMetadata={() => setIsPreviewLoading(true)}
                  onLoadedData={() => setIsPreviewLoading(false)}
                />
                {isPreviewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[4px] z-[9999]">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-10 h-10 text-teal-500 animate-spin shadow-lg" />
                      <span className="text-white text-[10px] font-bold uppercase tracking-widest opacity-80">Loading Preview...</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Modal Actions */}
            <div className="absolute top-4 right-4 z-50">
              <button
                onClick={() => setPreviewFile(null)}
                className="p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Bottom Controls */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <h3 className="text-white font-bold text-lg leading-tight truncate max-w-[300px]">
                  {previewFile?.name}
                </h3>
                <span className="text-white/60 text-xs uppercase font-bold tracking-widest">{previewFile?.type}</span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPreviewMuted(!previewMuted)}
                  className={`flex items-center gap-2 h-10 px-4 rounded-xl font-bold text-sm transition-all ${previewMuted
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                    }`}
                >
                  {previewMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  <span>{previewMuted ? 'Muted' : 'Sound On'}</span>
                </button>

                <button
                  onClick={() => {
                    if (previewFile) {
                      onAddToCanvas?.(previewFile, undefined, undefined, previewMuted);
                      setPreviewFile(null);
                    }
                  }}
                  className="flex items-center gap-2 h-10 px-6 bg-[#30A84B] hover:bg-[#288e3f] text-white rounded-xl font-bold transition-all shadow-lg active:scale-95"
                >
                  <Play className="w-4 h-4 fill-current" />
                  <span>Add to Canvas</span>
                </button>
              </div>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );
}
